import { useState, useMemo, useCallback, useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useDropzone } from "react-dropzone";
import { Upload, X, Check } from "lucide-react";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";

const SURFACES = [
  "Walls", "Ceilings", "Doors", "Trims", "Skirtings",
  "Architraves", "Fascia", "Gutters", "Roofing",
];

function dataUrlSize(dataUrl) {
  const i = dataUrl.indexOf(",");
  return Math.floor(((dataUrl.length - i - 1) * 3) / 4);
}

// Compress an image client-side so we keep base64 payloads small
async function compressImage(file, maxDim = 1280, quality = 0.78) {
  const dataUrl = await new Promise((res, rej) => {
    const r = new FileReader();
    r.onload = () => res(r.result);
    r.onerror = rej;
    r.readAsDataURL(file);
  });
  const img = await new Promise((res, rej) => {
    const im = new Image();
    im.onload = () => res(im);
    im.onerror = rej;
    im.src = dataUrl;
  });
  let { width, height } = img;
  if (Math.max(width, height) > maxDim) {
    const scale = maxDim / Math.max(width, height);
    width = Math.round(width * scale);
    height = Math.round(height * scale);
  }
  const canvas = document.createElement("canvas");
  canvas.width = width;
  canvas.height = height;
  canvas.getContext("2d").drawImage(img, 0, 0, width, height);
  return canvas.toDataURL("image/jpeg", quality);
}

export default function QuoteForm() {
  const { user } = useAuth();
  const nav = useNavigate();
  const [params] = useSearchParams();
  const fromProject = params.get("from_project");
  const painterSlug = (params.get("painter") || "").trim().toLowerCase() || null;
  const [step, setStep] = useState(1);
  const [submitting, setSubmitting] = useState(false);
  const [estimate, setEstimate] = useState(null);
  const [estimateBusy, setEstimateBusy] = useState(false);
  const [err, setErr] = useState("");
  const [projectNote, setProjectNote] = useState("");
  const [painterInfo, setPainterInfo] = useState(null);  // Block E: { slug, business_name, hq_suburb, hq_state, verified_since_label }
  const [projectSnapshot, setProjectSnapshot] = useState(null);  // Block E: { project_title, scheme_name, scheme_colours[], hero_render_id, scheme_id }
  const [snapshotImageUrl, setSnapshotImageUrl] = useState(null);  // Block E: blob object URL for the render preview

  // Block E — fetch the hero render bytes via axios (cookie-auth) so we
  // can display it without exposing the JWT. Cleaned up on unmount.
  useEffect(() => {
    if (!projectSnapshot?.hero_render_id) return;
    let alive = true;
    let url = null;
    api.get(`/visualiser/renders/${projectSnapshot.hero_render_id}/image`, { responseType: "blob" })
      .then((r) => {
        if (!alive) return;
        url = URL.createObjectURL(r.data);
        setSnapshotImageUrl(url);
      })
      .catch(() => { /* swallow */ });
    return () => {
      alive = false;
      if (url) URL.revokeObjectURL(url);
    };
  }, [projectSnapshot?.hero_render_id]);

  const [form, setForm] = useState({
    full_name: user?.name || "",
    email: user?.email || "",
    phone: user?.phone || "",
    address: "",
    suburb: "",
    project_scope: "interior",
    property_type: "house",
    stories: 1,
    bedrooms: 3,
    bathrooms: 2,
    living_areas: 1,
    surfaces: ["Walls"],
    surface_condition: "good",
    access_difficulty: "easy",
    scaffold_required: false,
    preferred_timeframe: "",
    notes: "",
  });
  const [photos, setPhotos] = useState([]); // { data, label }

  // Prefill from a visualiser project (also captures Block E snapshot for the preview card)
  useEffect(() => {
    if (!fromProject) return;
    api.get(`/visualiser/projects/${fromProject}/quote-prefill`)
      .then(({ data }) => {
        setProjectNote(`Linked to ColourMatch design "${data.title}"${data.latest_color_name ? ` · chosen colour: ${data.latest_color_name} (${data.latest_color_hex})` : ""}.`);
        if (data.snapshot) setProjectSnapshot(data.snapshot);
        setForm((s) => ({
          ...s,
          notes: s.notes
            ? `${s.notes}\n\nLinked design: ${data.title}${data.latest_color_name ? ` · ${data.latest_color_name} (${data.latest_color_hex})` : ""}`
            : `Linked to ColourMatch design "${data.title}"${data.latest_color_name ? ` · ${data.latest_color_name} (${data.latest_color_hex})` : ""}.`,
        }));
      })
      .catch(() => {});
  }, [fromProject]);

  // Block E — fetch painter info when ?painter=<slug> is present so we can
  // show "Sending to <Painter business name>" header. Silently no-op if the
  // slug is unknown or PAINTERPRO is OFF (api returns 404).
  useEffect(() => {
    if (!painterSlug) return;
    api.get(`/public/painters/${encodeURIComponent(painterSlug)}`)
      .then(({ data }) => {
        setPainterInfo({
          slug: data.slug,
          business_name: data.business_name,
          hq_suburb: data.hq_suburb,
          hq_state: data.hq_state,
          verified_since_label: data.verified_since_label,
        });
        // Auto-prefill suburb to the painter's HQ (homeowner can edit)
        setForm((s) => (s.suburb ? s : { ...s, suburb: data.hq_suburb || "" }));
      })
      .catch(() => {});
  }, [painterSlug]);

  const setF = (k, v) => setForm((s) => ({ ...s, [k]: v }));

  const toggleSurface = (s) =>
    setF("surfaces", form.surfaces.includes(s) ? form.surfaces.filter((x) => x !== s) : [...form.surfaces, s]);

  const onDrop = useCallback(async (accepted) => {
    setErr("");
    const remaining = 8 - photos.length;
    const slice = accepted.slice(0, remaining);
    const compressed = [];
    for (const f of slice) {
      try {
        const d = await compressImage(f);
        // hard cap each image at ~1.2MB
        if (dataUrlSize(d) > 1200000) {
          const d2 = await compressImage(f, 1024, 0.7);
          compressed.push({ data: d2, label: f.name });
        } else {
          compressed.push({ data: d, label: f.name });
        }
      } catch (e) {
        if (process.env.NODE_ENV !== "production") console.warn("Photo compress failed, skipping:", e?.message);
      }
    }
    setPhotos((p) => [...p, ...compressed]);
  }, [photos.length]);

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: { "image/*": [] },
    maxFiles: 8,
  });

  const removePhoto = (i) => setPhotos((p) => p.filter((_, idx) => idx !== i));

  const fetchEstimate = async () => {
    setEstimateBusy(true);
    try {
      const { data } = await api.post("/quotes/estimate", {
        project_scope: form.project_scope,
        property_type: form.property_type,
        stories: Number(form.stories),
        bedrooms: Number(form.bedrooms),
        bathrooms: Number(form.bathrooms),
        living_areas: Number(form.living_areas),
        surface_condition: form.surface_condition,
        access_difficulty: form.access_difficulty,
        scaffold_required: !!form.scaffold_required,
      });
      setEstimate(data);
    } catch (e) {
      setErr(formatApiError(e.response?.data?.detail) || e.message);
    } finally {
      setEstimateBusy(false);
    }
  };

  const submit = async () => {
    setSubmitting(true);
    setErr("");
    try {
      const payload = {
        ...form,
        stories: Number(form.stories),
        bedrooms: Number(form.bedrooms),
        bathrooms: Number(form.bathrooms),
        living_areas: Number(form.living_areas),
        surfaces: form.surfaces.map((s) => s.toLowerCase()),
        scaffold_required: !!form.scaffold_required,
        photos: photos.map((p) => ({ data: p.data, label: p.label })),
        estimate_low: estimate?.low ?? null,
        estimate_high: estimate?.high ?? null,
        // Block E — homeowner → painter handoff. All three are optional; the
        // backend silently drops anything it can't validate (unknown slug, no
        // ownership of the project, etc.).
        painter_slug: painterInfo?.slug || painterSlug || null,
        project_id: fromProject || null,
        selected_scheme_id: projectSnapshot?.scheme_id || null,
      };
      const { data } = await api.post("/quotes", payload);
      nav(`/dashboard?submitted=${data.id}`);
    } catch (e) {
      setErr(formatApiError(e.response?.data?.detail) || e.message);
    } finally {
      setSubmitting(false);
    }
  };

  const totalPhotoSize = useMemo(
    () => photos.reduce((s, p) => s + dataUrlSize(p.data), 0),
    [photos]
  );

  return (
    <div className="max-w-5xl mx-auto px-6 lg:px-10 py-12 lg:py-20" data-testid="quote-page">
      <div className="overline mb-3">Request a quote</div>
      <h1 className="font-serif text-4xl lg:text-5xl tracking-tight mb-3">Tell us about your project.</h1>
      <p className="text-brand-muted mb-10 max-w-2xl">
        Four short steps, no obligation. Add photos of any surfaces you&rsquo;d like painted &mdash;
        interior, exterior, or both.
      </p>

      {/* Block E — Sending to painter header */}
      {painterInfo && (
        <div
          data-testid="quote-painter-header"
          className="mb-6 bg-brand-ink text-white px-6 py-5 flex flex-col md:flex-row md:items-center gap-3"
        >
          <div className="flex-1">
            <div className="text-xs uppercase tracking-widest text-white/60">Sending to</div>
            <div className="text-xl font-serif mt-0.5" data-testid="quote-painter-name">
              {painterInfo.business_name}
            </div>
            <div className="text-xs text-white/70 mt-1">
              {painterInfo.hq_suburb}, {painterInfo.hq_state}
              {painterInfo.verified_since_label ? ` · ${painterInfo.verified_since_label}` : ""}
            </div>
          </div>
          <div className="text-xs text-white/70 md:text-right">
            Your contact details stay private<br />until they reply.
          </div>
        </div>
      )}

      {/* Block E — "Your project at a glance" preview card */}
      {projectSnapshot && (projectSnapshot.scheme_colours?.length > 0 || projectSnapshot.hero_render_id) && (
        <div
          data-testid="quote-project-preview"
          className="mb-6 bg-white border border-brand-linen p-6"
        >
          <div className="flex items-center justify-between mb-4">
            <div>
              <div className="overline">Your project at a glance</div>
              <div className="text-lg font-serif mt-1" data-testid="quote-project-title">
                {projectSnapshot.project_title || "Your ColourMatch design"}
              </div>
              {projectSnapshot.scheme_name && (
                <div className="text-xs text-brand-muted mt-0.5" data-testid="quote-scheme-name">
                  Scheme: <span className="font-medium text-brand-ink">{projectSnapshot.scheme_name}</span>
                </div>
              )}
            </div>
          </div>
          <div className="grid md:grid-cols-5 gap-4">
            {snapshotImageUrl && (
              <div className="md:col-span-2">
                <img
                  src={snapshotImageUrl}
                  alt="Project preview"
                  data-testid="quote-project-hero"
                  className="w-full h-32 md:h-40 object-cover border border-brand-linen"
                />
              </div>
            )}
            {projectSnapshot.scheme_colours?.length > 0 && (
              <div className="md:col-span-3" data-testid="quote-scheme-colours">
                <div className="overline mb-2 text-xs">Selected colours</div>
                <div className="space-y-1.5">
                  {projectSnapshot.scheme_colours.slice(0, 5).map((c, i) => (
                    <div key={`sc-${i}-${c.color_hex}`} className="flex items-center gap-2.5 text-xs">
                      <span
                        className="inline-block w-5 h-5 border border-brand-linen flex-shrink-0"
                        style={{ background: c.color_hex }}
                        data-testid={`quote-swatch-${i}`}
                      />
                      <span className="font-medium text-brand-ink">{c.color_name || c.color_hex}</span>
                      <span className="text-brand-muted">
                        {[c.surface, c.finish].filter(Boolean).join(" · ").replace(/_/g, " ")}
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
          <p className="text-xs text-brand-muted mt-4">
            {painterInfo
              ? `${painterInfo.business_name} will see your photo, colours and notes the moment you submit.`
              : "Your photo, colours and notes will be shared with the painter you choose."}
          </p>
        </div>
      )}

      {projectNote && !projectSnapshot && (
        <div className="mb-8 bg-brand-sage/5 border border-brand-sage/30 text-brand-sage px-5 py-4 text-sm" data-testid="project-link-banner">
          {projectNote}
        </div>
      )}

      {/* Stepper */}
      <div className="grid grid-cols-4 gap-px bg-brand-linen border border-brand-linen mb-10" data-testid="quote-stepper">
        {[
          { n: 1, label: "Property" },
          { n: 2, label: "Scope & Surfaces" },
          { n: 3, label: "Photos" },
          { n: 4, label: "Contact & Estimate" },
        ].map((s) => (
          <button
            key={s.n}
            onClick={() => setStep(s.n)}
            data-testid={`stepper-${s.n}`}
            className={`bg-white p-4 text-left transition-colors ${step === s.n ? "bg-brand-sage text-white" : "hover:bg-brand-linen/30"}`}
          >
            <div className={`text-xs uppercase tracking-widest mb-1 ${step === s.n ? "text-white/80" : "text-brand-muted"}`}>Step {s.n}</div>
            <div className={`text-sm ${step === s.n ? "text-white" : "text-brand-ink"}`}>{s.label}</div>
          </button>
        ))}
      </div>

      {err && (
        <div className="mb-6 text-sm border border-destructive/30 bg-destructive/5 text-destructive px-4 py-3" data-testid="quote-error">
          {err}
        </div>
      )}

      {/* STEP 1: Property */}
      {step === 1 && (
        <div className="bg-white border border-brand-linen p-8 lg:p-10 grid md:grid-cols-2 gap-6" data-testid="step-property">
          <div>
            <span className="overline block mb-2">Property type</span>
            <div className="grid grid-cols-2 gap-2">
              {[
                ["house", "House"],
                ["townhouse", "Townhouse"],
                ["apartment", "Apartment"],
                ["commercial", "Commercial"],
                ["other", "Other"],
              ].map(([v, l]) => (
                <button key={v} onClick={() => setF("property_type", v)}
                  data-testid={`property-type-${v}`}
                  className={`border px-3 py-3 text-sm text-left ${form.property_type === v ? "border-brand-sage bg-brand-sage text-white" : "border-brand-linen hover:bg-brand-linen/30"}`}>
                  {l}
                </button>
              ))}
            </div>
          </div>

          <label className="block">
            <span className="overline block mb-2">Number of storeys</span>
            <select value={form.stories} onChange={(e) => setF("stories", e.target.value)} data-testid="stories-select"
              className="w-full border border-brand-linen bg-white px-4 py-3 text-sm">
              {[1, 2, 3, 4, 5].map((n) => <option key={n} value={n}>{n}</option>)}
            </select>
          </label>

          <label className="block">
            <span className="overline block mb-2">Bedrooms</span>
            <input type="number" min="0" max="20" value={form.bedrooms}
              onChange={(e) => setF("bedrooms", e.target.value)} data-testid="bedrooms-input"
              className="w-full border border-brand-linen bg-white px-4 py-3 text-sm" />
          </label>
          <label className="block">
            <span className="overline block mb-2">Bathrooms</span>
            <input type="number" min="0" max="20" value={form.bathrooms}
              onChange={(e) => setF("bathrooms", e.target.value)} data-testid="bathrooms-input"
              className="w-full border border-brand-linen bg-white px-4 py-3 text-sm" />
          </label>
          <label className="block md:col-span-2">
            <span className="overline block mb-2">Living areas (kitchen, lounge, dining)</span>
            <input type="number" min="0" max="20" value={form.living_areas}
              onChange={(e) => setF("living_areas", e.target.value)} data-testid="living-areas-input"
              className="w-full border border-brand-linen bg-white px-4 py-3 text-sm" />
          </label>

          <div className="md:col-span-2 flex justify-end">
            <button onClick={() => setStep(2)} data-testid="step-next-1"
              className="bg-brand-sage text-white px-6 py-3 text-sm tracking-wide hover:bg-brand-sageDark">
              Continue
            </button>
          </div>
        </div>
      )}

      {/* STEP 2: Scope & Surfaces */}
      {step === 2 && (
        <div className="bg-white border border-brand-linen p-8 lg:p-10 grid md:grid-cols-2 gap-8" data-testid="step-scope">
          <div className="md:col-span-2">
            <span className="overline block mb-3">Project scope</span>
            <div className="grid grid-cols-3 gap-2">
              {[
                ["interior", "Interior"],
                ["exterior", "Exterior"],
                ["both", "Both"],
              ].map(([v, l]) => (
                <button key={v} onClick={() => setF("project_scope", v)}
                  data-testid={`scope-${v}`}
                  className={`border px-3 py-4 text-sm ${form.project_scope === v ? "border-brand-sage bg-brand-sage text-white" : "border-brand-linen hover:bg-brand-linen/30"}`}>
                  {l}
                </button>
              ))}
            </div>
          </div>

          <div className="md:col-span-2">
            <span className="overline block mb-3">Surfaces to paint</span>
            <div className="flex flex-wrap gap-2">
              {SURFACES.map((s) => (
                <button key={s} onClick={() => toggleSurface(s)}
                  data-testid={`surface-${s.toLowerCase()}`}
                  className={`border px-4 py-2 text-sm ${form.surfaces.includes(s) ? "border-brand-sage bg-brand-sage text-white" : "border-brand-linen hover:bg-brand-linen/30"}`}>
                  {s}
                </button>
              ))}
            </div>
          </div>

          <div>
            <span className="overline block mb-2">Surface condition</span>
            <div className="grid grid-cols-3 gap-2">
              {[["good", "Good"], ["fair", "Fair"], ["poor", "Poor"]].map(([v, l]) => (
                <button key={v} onClick={() => setF("surface_condition", v)}
                  data-testid={`condition-${v}`}
                  className={`border px-3 py-3 text-sm ${form.surface_condition === v ? "border-brand-sage bg-brand-sage text-white" : "border-brand-linen hover:bg-brand-linen/30"}`}>{l}</button>
              ))}
            </div>
            <p className="text-xs text-brand-muted mt-2">Fair / Poor adds preparation time.</p>
          </div>

          <div>
            <span className="overline block mb-2">Access difficulty</span>
            <div className="grid grid-cols-3 gap-2">
              {[["easy", "Easy"], ["moderate", "Moderate"], ["difficult", "Difficult"]].map(([v, l]) => (
                <button key={v} onClick={() => setF("access_difficulty", v)}
                  data-testid={`access-${v}`}
                  className={`border px-3 py-3 text-sm ${form.access_difficulty === v ? "border-brand-sage bg-brand-sage text-white" : "border-brand-linen hover:bg-brand-linen/30"}`}>{l}</button>
              ))}
            </div>
          </div>

          <label className="flex items-center gap-3 md:col-span-2 select-none">
            <input type="checkbox" checked={form.scaffold_required}
              onChange={(e) => setF("scaffold_required", e.target.checked)}
              data-testid="scaffold-checkbox"
              className="w-4 h-4 accent-brand-sage" />
            <span className="text-sm">Scaffold / elevated access required (multi-storey, high ceilings)</span>
          </label>

          <div className="md:col-span-2 flex justify-between">
            <button onClick={() => setStep(1)} data-testid="step-back-2"
              className="border border-brand-linen px-6 py-3 text-sm">Back</button>
            <button onClick={() => setStep(3)} data-testid="step-next-2"
              className="bg-brand-sage text-white px-6 py-3 text-sm tracking-wide hover:bg-brand-sageDark">Continue</button>
          </div>
        </div>
      )}

      {/* STEP 3: Photos */}
      {step === 3 && (
        <div className="bg-white border border-brand-linen p-8 lg:p-10" data-testid="step-photos">
          <span className="overline block mb-3">Photos (up to 8)</span>
          <div {...getRootProps()} data-testid="photo-dropzone"
            className={`border-2 border-dashed p-10 text-center cursor-pointer transition-colors ${isDragActive ? "border-brand-sage bg-brand-linen/40" : "border-brand-linen hover:bg-brand-linen/20"}`}>
            <input {...getInputProps()} data-testid="photo-input" />
            <Upload className="mx-auto mb-3 text-brand-sage" size={28} strokeWidth={1.5} />
            <p className="text-sm text-brand-ink">Drop photos here, or tap to choose</p>
            <p className="text-xs text-brand-muted mt-1">JPG / PNG · Compressed automatically · Max 8</p>
          </div>

          {photos.length > 0 && (
            <>
              <div className="overline mt-8 mb-3">Attached ({photos.length}) · {Math.round(totalPhotoSize / 1024)} KB</div>
              <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3">
                {photos.map((p, i) => (
                  <div key={`${p.label || "photo"}-${i}-${p.data.length}`} className="relative group border border-brand-linen">
                    <img src={p.data} alt="" className="w-full h-32 object-cover" />
                    <button onClick={() => removePhoto(i)} data-testid={`remove-photo-${i}`}
                      className="absolute top-1.5 right-1.5 bg-white border border-brand-linen p-1 opacity-0 group-hover:opacity-100 transition-opacity">
                      <X size={14} />
                    </button>
                  </div>
                ))}
              </div>
            </>
          )}

          <div className="mt-10 flex justify-between">
            <button onClick={() => setStep(2)} data-testid="step-back-3"
              className="border border-brand-linen px-6 py-3 text-sm">Back</button>
            <button onClick={() => setStep(4)} data-testid="step-next-3"
              className="bg-brand-sage text-white px-6 py-3 text-sm tracking-wide hover:bg-brand-sageDark">Continue</button>
          </div>
        </div>
      )}

      {/* STEP 4: Contact + Estimate */}
      {step === 4 && (
        <div className="grid md:grid-cols-5 gap-6" data-testid="step-contact">
          <div className="bg-white border border-brand-linen p-8 md:col-span-3 space-y-5">
            <h3 className="font-serif text-2xl">Your details</h3>
            <label className="block">
              <span className="overline block mb-2">Full name</span>
              <input required value={form.full_name} onChange={(e) => setF("full_name", e.target.value)}
                data-testid="contact-name-input"
                className="w-full border border-brand-linen bg-white px-4 py-3 text-sm" />
            </label>
            <div className="grid grid-cols-2 gap-4">
              <label className="block">
                <span className="overline block mb-2">Email</span>
                <input type="email" required value={form.email} onChange={(e) => setF("email", e.target.value)}
                  data-testid="contact-email-input"
                  className="w-full border border-brand-linen bg-white px-4 py-3 text-sm" />
              </label>
              <label className="block">
                <span className="overline block mb-2">Phone</span>
                <input required value={form.phone} onChange={(e) => setF("phone", e.target.value)}
                  data-testid="contact-phone-input"
                  className="w-full border border-brand-linen bg-white px-4 py-3 text-sm" />
              </label>
            </div>
            <label className="block">
              <span className="overline block mb-2">Property address</span>
              <input required value={form.address} onChange={(e) => setF("address", e.target.value)}
                data-testid="contact-address-input"
                className="w-full border border-brand-linen bg-white px-4 py-3 text-sm" />
            </label>
            <label className="block">
              <span className="overline block mb-2">Suburb / city</span>
              <input value={form.suburb} onChange={(e) => setF("suburb", e.target.value)}
                data-testid="contact-suburb-input"
                className="w-full border border-brand-linen bg-white px-4 py-3 text-sm" />
            </label>
            <label className="block">
              <span className="overline block mb-2">Preferred timeframe</span>
              <select value={form.preferred_timeframe} onChange={(e) => setF("preferred_timeframe", e.target.value)}
                data-testid="contact-timeframe-select"
                className="w-full border border-brand-linen bg-white px-4 py-3 text-sm">
                <option value="">Flexible</option>
                <option value="urgent">Within 2 weeks</option>
                <option value="month">Within a month</option>
                <option value="quarter">Within 3 months</option>
                <option value="planning">Just planning ahead</option>
              </select>
            </label>
            <label className="block">
              <span className="overline block mb-2">Anything else?</span>
              <textarea rows={4} value={form.notes} onChange={(e) => setF("notes", e.target.value)}
                data-testid="contact-notes-input"
                className="w-full border border-brand-linen bg-white px-4 py-3 text-sm" />
            </label>
          </div>

          <div className="md:col-span-2 space-y-4">
            <div className="bg-brand-sage text-white p-8" data-testid="estimate-card">
              <div className="overline text-white/70 mb-3">Smart estimate</div>
              {estimate ? (
                <>
                  <div className="font-serif text-4xl tracking-tight">
                    ${estimate.low.toLocaleString()} – ${estimate.high.toLocaleString()}
                  </div>
                  <div className="text-xs text-white/70 mt-1">AUD · indicative range</div>
                  <ul className="mt-6 space-y-2 text-xs text-white/85">
                    {estimate.breakdown.map((b, i) => (
                      <li key={`bk-${i}-${b.slice(0, 20)}`} className="flex gap-2"><Check size={12} className="mt-0.5 flex-shrink-0" /> <span>{b}</span></li>
                    ))}
                  </ul>
                </>
              ) : (
                <>
                  <div className="font-serif text-3xl tracking-tight">Get your range</div>
                  <p className="text-sm text-white/80 mt-3">Tap below for an instant estimate range based on your inputs.</p>
                </>
              )}
              <button onClick={fetchEstimate} disabled={estimateBusy} data-testid="calc-estimate-btn"
                className="mt-6 w-full bg-white text-brand-sage py-3 text-sm tracking-wide hover:bg-brand-linen disabled:opacity-60">
                {estimateBusy ? "Calculating…" : estimate ? "Recalculate" : "Calculate estimate"}
              </button>
            </div>
            <p className="text-xs text-brand-muted">
              This is a guide only. A ColourMatch specialist will confirm a final, itemised quote based on a site visit or detailed photos.
            </p>
          </div>

          <div className="md:col-span-5 flex justify-between">
            <button onClick={() => setStep(3)} data-testid="step-back-4"
              className="border border-brand-linen px-6 py-3 text-sm">Back</button>
            <button onClick={submit} disabled={submitting} data-testid="submit-quote-btn"
              className="bg-brand-sage text-white px-8 py-3 text-sm tracking-wide hover:bg-brand-sageDark disabled:opacity-60">
              {submitting ? "Submitting…" : "Submit quote request"}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
